import React, { Suspense } from 'react';
import lazyWithModuleBoundary from './utils/lazyWithModuleBoundary';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import MFAEnrollment from './components/MFAEnrollment';
import { safeWatchlistM365SettingsReturn } from './utils/watchlistM365Callback';

import { isFdlAdminEnabled, isMsiAdminEnabled } from './features/admin/shared/config';

const SecurityIdentityAdminRoute = lazyWithModuleBoundary(() => import('./features/admin/security-identity/SecurityIdentityAdminRoute'));
const FactorDataAdminRoute = lazyWithModuleBoundary(() => import('./features/admin/factor-data/FactorDataAdminRoute'));
const MosekLicenseAdminRoute = lazyWithModuleBoundary(() => import('./features/admin/mosek-license/MosekLicenseAdminRoute'));
const MarketDataAdminRoute = lazyWithModuleBoundary(() => import('./features/admin/market-data/MarketDataAdminRoute'));

const PrivateRoute = ({ children, user, hasMfaEnrolled }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // hasMfaEnrolled is null while enrollment status is still loading; render
  // children rather than redirecting until we know for certain.
  if (hasMfaEnrolled === null) {
    return children;
  }

  if (hasMfaEnrolled === false) {
    return <Navigate to="/mfa-enrollment" replace />;
  }

  return children;
};

const PublicRoute = ({ children, user }) => {
  if (user) {
    return <Navigate to="/dashboard/overview" replace />;
  }
  return children;
};

const WatchlistM365SettingsReturn = () => {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{
        pathname: '/dashboard/overview',
        search: safeWatchlistM365SettingsReturn(location.search),
      }}
    />
  );
};

function AppRoutes({
  user,
  isAdmin = false,
  currentEnv = 'dev',
  userData,
  darkMode,
  compactMode,
  hasMfaEnrolled,
  onUserDataChange,
  newsRecencyHours,
  deepDiveFocusAreas,
  maxSharpeSkill = null,
  setMaxSharpeSkill = null,
  compareExploreColumns = null,
  setCompareExploreColumns = null,
  securityOperationStatus,
  watchlistPublicationState,
  onRegisterWatchlistPublicationExecutor,
  onCommitWatchlistBootstrapRevision,
  securityOperationSessionScopeToken,
  exclusiveCoreLoadOwnerRef,
}) {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute user={user}>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/mfa-enrollment"
        element={
          <PrivateRoute user={user} hasMfaEnrolled={hasMfaEnrolled}>
            <MFAEnrollment />
          </PrivateRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <PrivateRoute user={user} hasMfaEnrolled={hasMfaEnrolled}>
            <Dashboard
              user={user}
              userData={userData}
              onUserDataChange={onUserDataChange}
              darkMode={darkMode}
              compactMode={compactMode}
              newsRecencyHours={newsRecencyHours}
              deepDiveFocusAreas={deepDiveFocusAreas}
              maxSharpeSkill={maxSharpeSkill}
              setMaxSharpeSkill={setMaxSharpeSkill}
              compareExploreColumns={compareExploreColumns}
              setCompareExploreColumns={setCompareExploreColumns}
              securityOperationStatus={securityOperationStatus}
              watchlistPublicationState={watchlistPublicationState}
              onRegisterWatchlistPublicationExecutor={onRegisterWatchlistPublicationExecutor}
              onCommitWatchlistBootstrapRevision={onCommitWatchlistBootstrapRevision}
              securityOperationSessionScopeToken={securityOperationSessionScopeToken}
              exclusiveCoreLoadOwnerRef={exclusiveCoreLoadOwnerRef}
            />
          </PrivateRoute>
        }
      >
        <Route path="overview" element={null} />
        <Route path="watchlist" element={null} />
        <Route path="pos-sizing" element={null} />
        <Route path="optimizer" element={null} />
        <Route path="performance" element={null} />
        <Route index element={<Navigate to="overview" replace />} />
      </Route>

      <Route
        path="/settings"
        element={
          <PrivateRoute user={user} hasMfaEnrolled={hasMfaEnrolled}>
            <WatchlistM365SettingsReturn />
          </PrivateRoute>
        }
      />

      {isFdlAdminEnabled() && (
        <Route path="/admin/factor-data" element={
          <PrivateRoute user={user} hasMfaEnrolled={hasMfaEnrolled}>
            <Suspense fallback={null}>
              <FactorDataAdminRoute user={user} isAdmin={isAdmin} environment={currentEnv} />
            </Suspense>
          </PrivateRoute>
        } />
      )}
      {isMsiAdminEnabled() && (
        <Route path="/admin/security-identity" element={
          <PrivateRoute user={user} hasMfaEnrolled={hasMfaEnrolled}>
            <Suspense fallback={null}>
              <SecurityIdentityAdminRoute user={user} isAdmin={isAdmin} environment={currentEnv} />
            </Suspense>
          </PrivateRoute>
        } />
      )}
      <Route path="/admin/mosek-license" element={
        <PrivateRoute user={user} hasMfaEnrolled={hasMfaEnrolled}>
          <Suspense fallback={null}>
            <MosekLicenseAdminRoute user={user} isAdmin={isAdmin} environment={currentEnv} />
          </Suspense>
        </PrivateRoute>
      } />
      <Route path="/admin/market-data" element={
        <PrivateRoute user={user} hasMfaEnrolled={hasMfaEnrolled}>
          <Suspense fallback={null}>
            <MarketDataAdminRoute user={user} isAdmin={isAdmin} environment={currentEnv} />
          </Suspense>
        </PrivateRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard/overview" replace />} />
      <Route path="*" element={<Navigate to="/dashboard/overview" replace />} />
    </Routes>
  );
}

export default AppRoutes;
